import config from '../../config';

const AUDIENCE_LAB_URL = 'https://audiencelab.attractr.live';

const separator = key => ({ key, kind: 'separator' });

/**
 * The rows of a member's menu: the popover under the identity button on desktop
 * (`layout: 'popover'`) and the bottom sheet on phones (`layout: 'sheet'`). Both components render
 * the same list, so the links and the role rules live here once.
 *
 * - The person's own identity comes first ("Signed in as"). Slice A adds the identities they can
 *   act as (creator profile, organizations) and the disclaimer in the same place.
 * - Creators and organizations get their dashboard (DashboardPage picks which one); everyone else
 *   gets "+ Add a creator profile or organization" (role selection).
 * - Community-only mode (REACT_APP_BETA_MODE) keeps its short list: account, Audience Lab and the
 *   legal pages.
 *
 * A row is { key, kind, labelId, … }: kind is 'caption', 'identity', 'separator', 'link'
 * (linkProps for NamedLink), 'external' (href) or 'logout'. `icon` (a name from Icon's set)
 * and `hintId` are the popover's icon and grey hint, `badge` shows the unread count and `tone`
 * is 'add' or 'quiet'.
 */
export const accountMenuRows = ({ userRole, isBetaMode = false, layout = 'popover' }) => {
  const isPopover = layout === 'popover';
  const identity = [
    { key: 'signedInAs', kind: 'caption', labelId: 'TopbarDesktop.signedInAs' },
    { key: 'identity', kind: 'identity' },
    separator('sepIdentity'),
  ];
  const logout = { key: 'logout', kind: 'logout', labelId: 'TopbarDesktop.logout', tone: 'quiet' };

  if (isBetaMode) {
    const legal = [
      ['PrivacyPolicyPage', 'TopbarDesktop.privacyPolicyLink'],
      ['TermsOfServicePage', 'TopbarDesktop.termsOfServiceLink'],
      ['CommunityGuidelinesPage', 'TopbarDesktop.communityGuidelinesLink'],
    ].map(([name, labelId]) => ({ key: name, kind: 'link', labelId, linkProps: { name } }));
    return [
      ...identity,
      {
        key: 'account',
        kind: 'link',
        labelId: 'TopbarDesktop.accountLink',
        linkProps: { name: 'AccountSettingsPage' },
      },
      {
        key: 'audienceLab',
        kind: 'external',
        labelId: 'TopbarDesktop.audienceLabLink',
        href: AUDIENCE_LAB_URL,
      },
      ...legal,
      separator('sepLogout'),
      logout,
    ];
  }

  const isCreator =
    userRole === config.userRoles.creator || userRole === config.userRoles.organization;
  const dashboardLabelId =
    userRole === config.userRoles.organization
      ? 'TopbarDesktop.switchToOrgDashboard'
      : 'TopbarDesktop.creatorDashboard';

  const addProfile = {
    key: 'addProfile',
    kind: 'link',
    labelId: 'TopbarDesktop.addProfile',
    linkProps: { name: 'RoleSelectionPage' },
    icon: 'add',
    tone: 'add',
  };
  // The popover lists the dashboards under a caption; the sheet has one "Dashboards" row.
  const dashboards = !isCreator
    ? [addProfile]
    : isPopover
    ? [
        { key: 'dashboardsCaption', kind: 'caption', labelId: 'TopbarDesktop.dashboards' },
        {
          key: 'dashboard',
          kind: 'link',
          labelId: dashboardLabelId,
          linkProps: { name: 'DashboardPage' },
        },
      ]
    : [
        {
          key: 'dashboard',
          kind: 'link',
          labelId: 'TopbarDesktop.dashboards',
          linkProps: { name: 'DashboardPage' },
        },
      ];

  return [
    ...identity,
    {
      key: 'myAttractr',
      kind: 'link',
      labelId: 'TopbarDesktop.myAttractr',
      hintId: isPopover ? 'TopbarDesktop.myAttractrHint' : null,
      icon: 'myAttractr',
      linkProps: { name: 'ManageSubscriptionsPage' },
    },
    {
      key: 'messages',
      kind: 'link',
      labelId: 'TopbarDesktop.inboxLink',
      icon: 'mail',
      badge: isPopover,
      linkProps: { name: 'AudienceConversationsPage' },
    },
    {
      key: 'account',
      kind: 'link',
      labelId: 'TopbarDesktop.accountLink',
      icon: 'settings',
      linkProps: { name: 'AccountSettingsPage' },
    },
    ...(isPopover ? [separator('sepDashboards')] : []),
    ...dashboards,
    ...(isPopover
      ? [
          separator('sepHelp'),
          {
            key: 'help',
            kind: 'link',
            labelId: 'TopbarDesktop.helpLink',
            linkProps: { name: 'HelpPage' },
            tone: 'quiet',
          },
        ]
      : []),
    logout,
  ];
};
